import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  slashAppErrorMessage,
  slashAppMonthly,
} from "../../utils/slashAppMonthly";

// マンスリーレッスンの「シフト調整」タブ。
// 講師が出した「出られる日」を 行=講師 / 列=候補日（土日祝・5週目）の表で見て、
// マスから回を登録し、講師ごとに「確定して通知」する。
// 書き込みと講師への通知はすべて slash-app の管理者APIが行う。
const WEEK = ["日", "月", "火", "水", "木", "金", "土"];

const STATUS = {
  submitted: { label: "提出済み", className: "is-submitted" },
  no_days: { label: "出られる日なし", className: "is-nodays" },
  not_submitted: { label: "未提出", className: "is-missing" },
  decided: { label: "日程決定済(提出不要)", className: "is-decided" },
};

const pad = (n) => String(n).padStart(2, "0");

function dayLabel(iso) {
  const d = new Date(`${iso}T00:00:00`);
  return `${d.getMonth() + 1}/${d.getDate()}(${WEEK[d.getDay()]})`;
}

function monthLabel(ym) {
  return `${parseInt(ym.slice(5, 7), 10)}月`;
}

function formatDateTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return `${d.getMonth() + 1}/${d.getDate()} ${pad(d.getHours())}:${pad(
    d.getMinutes(),
  )}`;
}

const toMinutes = (hhmm) => {
  const m = /^(\d{2}):(\d{2})$/.exec(hhmm || "");
  return m ? parseInt(m[1], 10) * 60 + parseInt(m[2], 10) : null;
};

const fromMinutes = (total) =>
  total >= 24 * 60
    ? null
    : `${pad(Math.floor(total / 60))}:${pad(total % 60)}`;

const dayText = (d) => (d.all_day ? "終日" : `${d.time_from}–${d.time_to}`);

// 前の回の終わりと同時に始めるのは重なりとみなさない（サーバーと同じ判定）。
const overlaps = (aStart, aEnd, bStart, bEnd) => aStart < bEnd && bStart < aEnd;

// 候補日の見出しの補足（祝日名・5週目）。土日は曜日で分かるので出さない。
const extraLabel = (label) =>
  label === "土" || label === "日" ? "" : label.replace(/^[土日]・/, "");

function MonthlyShiftAdjustPanel({ onChanged }) {
  const [board, setBoard] = useState(null);
  const [month, setMonth] = useState(null); // null = サーバーにおまかせ
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(false);

  const [registerTarget, setRegisterTarget] = useState(null); // { row, date }
  const [editTarget, setEditTarget] = useState(null); // row

  const load = useCallback(async (m) => {
    setLoading(true);
    setError(null);
    try {
      const data = await slashAppMonthly.shiftBoard(m || undefined);
      setBoard(data);
      setMonth(data.month);
    } catch (e) {
      console.error(e);
      setError(slashAppErrorMessage(e, "シフトの取得に失敗しました"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(null);
  }, [load]);

  const reload = useCallback(async () => {
    await load(month);
    if (onChanged) onChanged();
  }, [load, month, onChanged]);

  const confirm = useCallback(
    async (row) => {
      if (busy || !board) return;
      const n = row.lessons.filter((l) => !l.canceled).length;
      const ok = window.confirm(
        `${row.username} に${monthLabel(board.month)}分の日程の確定を` +
          `${row.submission.confirmed_at ? "再" : ""}通知します。\n\n` +
          `・${n > 0 ? `日程決定（全${n}回）` : "開催なし"}\n\n` +
          `講師のアプリにお知らせとプッシュ通知が届きます。よろしいですか？`,
      );
      if (!ok) return;
      setBusy(true);
      setError(null);
      setNotice(null);
      try {
        const res = await slashAppMonthly.confirm(board.month, [row.id]);
        if (res.failed) {
          setError(`${row.username} への通知に失敗しました。もう一度お試しください。`);
        } else {
          setNotice(`${row.username} に通知しました。`);
        }
        await reload();
      } catch (e) {
        console.error(e);
        setError(slashAppErrorMessage(e, "通知に失敗しました"));
      } finally {
        setBusy(false);
      }
    },
    [busy, board, reload],
  );

  const deleteSubmission = useCallback(
    async (row) => {
      if (busy || !board) return;
      const ok = window.confirm(
        `${row.username} の${monthLabel(board.month)}分のシフト提出を削除します。\n\n` +
          `・登録済みの回は消えません\n` +
          `・講師への通知はありません\n` +
          `・講師はアプリから提出し直せます（受付中の月で、その月の回がまだ無い場合のみ）\n\n` +
          `よろしいですか？`,
      );
      if (!ok) return;
      setBusy(true);
      setError(null);
      setNotice(null);
      try {
        await slashAppMonthly.deleteSubmission(row.id, board.month);
        await reload();
      } catch (e) {
        console.error(e);
        setError(slashAppErrorMessage(e, "削除に失敗しました"));
      } finally {
        setBusy(false);
      }
    },
    [busy, board, reload],
  );

  if (!board) {
    return (
      <div className="panel shift-adjust">
        {error ? <div className="error-banner">{error}</div> : null}
        <div className="empty-state">
          <p className="ja">{loading ? "読み込み中…" : "シフトを表示できません"}</p>
          <p className="en">Shift</p>
        </div>
      </div>
    );
  }

  return (
    <div className="shift-adjust">
      <div className="sa-toolbar">
        <div className="tabs">
          {board.months.map((m) => (
            <button
              key={m}
              type="button"
              className={`tab ${m === board.month ? "active" : ""}`}
              onClick={() => m !== board.month && !loading && load(m)}
            >
              {monthLabel(m)}分
            </button>
          ))}
        </div>
        <span className="sa-deadline">
          締切 {dayLabel(board.deadline)}
          {board.is_open ? "（受付中）" : "（締切済み）"}
        </span>
      </div>

      {error ? <div className="error-banner">{error}</div> : null}
      {notice ? <div className="sa-notice">{notice}</div> : null}

      <p className="sa-help">
        マスを押すとその日の回を登録できます。登録した回はすぐにホームページに掲載されます。
        日程が決まったら「確定して通知」で講師へ知らせてください。
      </p>

      <div className="panel sa-scroll">
        {board.instructors.length === 0 ? (
          <div className="empty-state">
            <p className="ja">マンスリーのクラスを持つ講師がいません</p>
            <p className="en">No Instructors</p>
          </div>
        ) : (
          <table className="sa-table">
            <thead>
              <tr>
                <th className="sa-inst-col">講師</th>
                {board.candidates.map((c) => {
                  const extra = extraLabel(c.label);
                  const d = new Date(`${c.date}T00:00:00`);
                  const holiday = extra
                    .split("・")
                    .some((v) => v && v !== "5週目");
                  return (
                    <th
                      key={c.date}
                      className={`sa-day-col ${
                        d.getDay() === 0 || holiday ? "is-sun" : ""
                      } ${d.getDay() === 6 && !holiday ? "is-sat" : ""}`}
                    >
                      <span className="sa-day">{dayLabel(c.date)}</span>
                      {extra ? <span className="sa-day-extra">{extra}</span> : null}
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {board.instructors.map((row) => (
                <InstructorRow
                  key={row.id}
                  row={row}
                  candidates={board.candidates}
                  busy={busy}
                  onCell={(date) => setRegisterTarget({ row, date })}
                  onEdit={() => setEditTarget(row)}
                  onDelete={() => deleteSubmission(row)}
                  onConfirm={() => confirm(row)}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      {registerTarget ? (
        <RegisterModal
          row={registerTarget.row}
          date={registerTarget.date}
          onClose={() => setRegisterTarget(null)}
          onDone={async (count) => {
            setRegisterTarget(null);
            setNotice(
              `${registerTarget.row.username} の ${dayLabel(
                registerTarget.date,
              )} に${count}件の回を登録しました。`,
            );
            await reload();
          }}
        />
      ) : null}

      {editTarget ? (
        <EditSubmissionModal
          row={editTarget}
          month={board.month}
          candidates={board.candidates}
          onClose={() => setEditTarget(null)}
          onDone={async () => {
            setEditTarget(null);
            setNotice(`${editTarget.username} の提出内容を保存しました。`);
            await reload();
          }}
        />
      ) : null}
    </div>
  );
}

function InstructorRow({
  row,
  candidates,
  busy,
  onCell,
  onEdit,
  onDelete,
  onConfirm,
}) {
  const status = STATUS[row.status] || STATUS.not_submitted;
  const sub = row.submission;
  const daysByDate = useMemo(() => {
    const map = {};
    (sub?.days || []).forEach((d) => {
      map[d.date] = d;
    });
    return map;
  }, [sub]);
  const lessonsByDate = useMemo(() => {
    const map = {};
    row.lessons.forEach((l) => {
      (map[l.date] = map[l.date] || []).push(l);
    });
    return map;
  }, [row.lessons]);
  const canRegister = row.classes.length > 0;

  return (
    <tr>
      <th className="sa-inst-col">
        <div className="sa-inst">
          <div className="sa-inst-name">
            {row.username}
            {row.dancer_name && row.dancer_name !== row.username ? (
              <span className="sa-inst-sub">{row.dancer_name}</span>
            ) : null}
          </div>
          <span className={`sa-status ${status.className}`}>{status.label}</span>
          {sub ? (
            <div className="sa-inst-meta">
              提出 {formatDateTime(sub.submitted_at)}
              {sub.confirmed_at ? (
                <span className="sa-confirmed">
                  ・確定通知 {formatDateTime(sub.confirmed_at)}
                </span>
              ) : sub.days.length > 0 ? (
                <span className="sa-unconfirmed">・未確定</span>
              ) : null}
            </div>
          ) : null}
          {sub?.note ? (
            <div className="sa-note" title={sub.note}>
              備考：{sub.note}
            </div>
          ) : null}
          <div className="sa-inst-actions">
            <button
              type="button"
              className="act act-ghost"
              onClick={onEdit}
              disabled={busy}
            >
              {sub ? "提出内容を編集" : "代理で提出"}
            </button>
            {sub ? (
              <>
                <button
                  type="button"
                  className="act act-ng"
                  onClick={onDelete}
                  disabled={busy}
                >
                  削除
                </button>
                <button
                  type="button"
                  className="act act-go"
                  onClick={onConfirm}
                  disabled={busy}
                >
                  {sub.confirmed_at ? "再通知" : "確定して通知"}
                </button>
              </>
            ) : null}
          </div>
        </div>
      </th>
      {candidates.map((c) => {
        const avail = daysByDate[c.date];
        const lessons = lessonsByDate[c.date] || [];
        return (
          <td
            key={c.date}
            className={`sa-cell ${avail ? "is-available" : ""} ${
              canRegister ? "is-clickable" : ""
            }`}
            onClick={canRegister ? () => onCell(c.date) : undefined}
            title={canRegister ? "この日の回を登録" : undefined}
          >
            {avail ? <span className="sa-avail">{dayText(avail)}</span> : null}
            {lessons.map((l) => (
              <span
                key={l.id}
                className={`sa-lesson ${l.canceled ? "is-canceled" : ""}`}
                title={`${l.name} ${l.start_time}–${l.end_time}（${l.studio_name}）${
                  l.canceled ? " 中止" : ""
                }`}
              >
                {l.start_time} {l.name}
              </span>
            ))}
          </td>
        );
      })}
    </tr>
  );
}

// 回の登録。クラスを複数選ぶと、前の回の終わりに次の回が始まるよう開始時刻を入れる。
function RegisterModal({ row, date, onClose, onDone }) {
  const avail = (row.submission?.days || []).find((d) => d.date === date);
  // 選んだ順に並べる。{ source_lesson_id, start, capacity, price, member_price }
  const [items, setItems] = useState([]);
  const [slots, setSlots] = useState([]);
  const [slotsError, setSlotsError] = useState(null);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const classById = useMemo(() => {
    const map = {};
    row.classes.forEach((c) => {
      map[c.source_lesson_id] = c;
    });
    return map;
  }, [row.classes]);

  // その日の埋まり枠。講師のクラスがあるスタジオごとに取ってまとめる。
  useEffect(() => {
    let alive = true;
    const studioIds = [...new Set(row.classes.map((c) => c.studio_id))];
    Promise.all(
      studioIds.map((sid) => slashAppMonthly.daySchedule(row.id, sid, date)),
    )
      .then((results) => {
        if (!alive) return;
        const seen = new Set();
        const merged = [];
        results.forEach((r) =>
          r.slots.forEach((s) => {
            const key = `${s.kind}-${s.studio_id}-${s.name}-${s.start_time}`;
            if (seen.has(key)) return;
            seen.add(key);
            merged.push(s);
          }),
        );
        merged.sort((a, b) => a.start_time.localeCompare(b.start_time));
        setSlots(merged);
      })
      .catch((e) => {
        console.error(e);
        if (alive)
          setSlotsError(slashAppErrorMessage(e, "この日の予定を取得できませんでした"));
      });
    return () => {
      alive = false;
    };
  }, [row.id, row.classes, date]);

  const endOf = (item) => {
    const start = toMinutes(item.start);
    if (start === null) return null;
    return fromMinutes(start + classById[item.source_lesson_id].duration_minutes);
  };

  const toggle = (cls) => {
    setItems((prev) => {
      if (prev.some((i) => i.source_lesson_id === cls.source_lesson_id)) {
        return prev.filter((i) => i.source_lesson_id !== cls.source_lesson_id);
      }
      // 前の回の終わりを開始時刻の初期値にする（続けて行うクラス向け）。
      const last = prev[prev.length - 1];
      const start = last
        ? endOf(last) || ""
        : avail && !avail.all_day
          ? avail.time_from
          : "";
      return [
        ...prev,
        {
          source_lesson_id: cls.source_lesson_id,
          start,
          capacity: String(cls.capacity),
          price: String(cls.price),
          member_price: cls.member_price === null ? "" : String(cls.member_price),
        },
      ];
    });
  };

  const update = (id, patch) =>
    setItems((prev) =>
      prev.map((i) => (i.source_lesson_id === id ? { ...i, ...patch } : i)),
    );

  // 入力の検査。重なりは登録できない（赤）、提出した時間帯から外れるのは注意（黄）。
  const checks = items.map((item, idx) => {
    const cls = classById[item.source_lesson_id];
    const errors = [];
    const warnings = [];
    const start = toMinutes(item.start);
    const endText = endOf(item);
    if (start === null) {
      errors.push("開始時刻を入力してください");
    } else if (!endText) {
      errors.push("終了時刻が日付をまたぎます");
    } else {
      const end = toMinutes(endText);
      slots.forEach((s) => {
        if (overlaps(start, end, toMinutes(s.start_time), toMinutes(s.end_time))) {
          errors.push(
            `${s.start_time}–${s.end_time}「${s.name}」（${
              s.is_own ? "講師本人" : s.instructor_name
            }・${s.studio_name}）と重なっています`,
          );
        }
      });
      items.slice(0, idx).forEach((other) => {
        const os = toMinutes(other.start);
        const oe = toMinutes(endOf(other) || "");
        if (os !== null && oe !== null && overlaps(start, end, os, oe)) {
          errors.push(`「${classById[other.source_lesson_id].name}」と重なっています`);
        }
      });
      if (!avail) {
        warnings.push("この日は出られる日として提出されていません");
      } else if (
        !avail.all_day &&
        (start < toMinutes(avail.time_from) || end > toMinutes(avail.time_to))
      ) {
        warnings.push(
          `講師が出した時間帯（${avail.time_from}–${avail.time_to}）から外れています`,
        );
      }
    }
    if (!(parseInt(item.capacity, 10) >= 1)) errors.push("定員は1以上にしてください");
    if (!(parseInt(item.price, 10) >= 0)) errors.push("料金を入力してください");
    if (item.member_price !== "" && !(parseInt(item.member_price, 10) >= 0)) {
      errors.push("会員価格を正しく入力してください");
    }
    return { cls, errors, warnings, endText };
  });
  const hasError = checks.some((c) => c.errors.length > 0);
  const hasWarning = checks.some((c) => c.warnings.length > 0);

  const submit = async () => {
    if (sending || items.length === 0 || hasError) return;
    if (
      hasWarning &&
      !window.confirm("講師が出した日時から外れている回があります。登録しますか？")
    ) {
      return;
    }
    setSending(true);
    setError(null);
    try {
      const res = await slashAppMonthly.registerLessons({
        instructor_id: row.id,
        date,
        lessons: items.map((i) => ({
          source_lesson_id: i.source_lesson_id,
          start_time: i.start,
          capacity: parseInt(i.capacity, 10),
          price: parseInt(i.price, 10),
          member_price: i.member_price === "" ? null : parseInt(i.member_price, 10),
        })),
      });
      await onDone(res.created_ids.length);
    } catch (e) {
      console.error(e);
      setError(slashAppErrorMessage(e, "登録に失敗しました"));
      setSending(false);
    }
  };

  const close = () => {
    if (!sending) onClose();
  };

  return (
    <div
      className="review-overlay"
      onClick={(e) => e.target === e.currentTarget && close()}
    >
      <div className="review-modal sa-modal" role="dialog" aria-modal="true">
        <div className="review-modal-head">
          <h3>
            {dayLabel(date)} {row.username} の回を登録
          </h3>
          <button type="button" className="review-x" onClick={close} aria-label="閉じる">
            ×
          </button>
        </div>
        <div className="review-modal-body">
          <p className="sa-avail-line">
            提出：
            {avail ? (
              <strong>{avail.all_day ? "終日OK" : `${avail.time_from}–${avail.time_to}`}</strong>
            ) : (
              <span className="sa-warn-text">出られる日として提出されていません</span>
            )}
          </p>

          <div className="sa-slots">
            <div className="review-label">この日の予定</div>
            {slotsError ? (
              <p className="sa-error-text">{slotsError}</p>
            ) : slots.length === 0 ? (
              <p className="review-note">埋まっている時間帯はありません</p>
            ) : (
              <ul>
                {slots.map((s, i) => (
                  <li key={`${s.kind}-${s.studio_id}-${s.start_time}-${i}`}>
                    <span className="sa-slot-time">
                      {s.start_time}–{s.end_time}
                    </span>
                    {s.kind === "monthly" ? "マンスリー" : s.kind === "makeup" ? "振替" : "通常"}
                    「{s.name}」 {s.is_own ? "講師本人" : s.instructor_name}・{s.studio_name}
                    {s.canceled ? "（休講日）" : ""}
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="review-label">レッスン</div>
          <div className="sa-classes">
            {row.classes.map((cls) => {
              const idx = items.findIndex(
                (i) => i.source_lesson_id === cls.source_lesson_id,
              );
              const item = items[idx];
              const check = idx >= 0 ? checks[idx] : null;
              return (
                <div
                  key={cls.source_lesson_id}
                  className={`sa-class ${item ? "is-on" : ""}`}
                >
                  <label className="sa-class-head">
                    <input
                      type="checkbox"
                      checked={!!item}
                      onChange={() => toggle(cls)}
                      disabled={sending}
                    />
                    <span className="sa-class-name">{cls.name}</span>
                    <span className="sa-class-sub">
                      {cls.studio_name}・{cls.duration_minutes}分
                    </span>
                  </label>
                  {item ? (
                    <div className="sa-fields">
                      {/* 入力欄は常に1行。エラーの有無で折り返し位置が変わらないようにする */}
                      <div className="sa-field-row">
                        <label>
                          開始
                          <input
                            type="time"
                            step={300}
                            value={item.start}
                            onChange={(e) =>
                              update(cls.source_lesson_id, { start: e.target.value })
                            }
                          />
                        </label>
                        <span className="sa-end">〜 {check.endText || "--:--"}</span>
                        <label className="sa-num is-short">
                          定員
                          <input
                            type="number"
                            min={1}
                            value={item.capacity}
                            onChange={(e) =>
                              update(cls.source_lesson_id, { capacity: e.target.value })
                            }
                          />
                        </label>
                        <label className="sa-num">
                          料金
                          <input
                            type="number"
                            min={0}
                            step={100}
                            value={item.price}
                            onChange={(e) =>
                              update(cls.source_lesson_id, { price: e.target.value })
                            }
                          />
                        </label>
                        <label className="sa-num">
                          会員価格
                          <input
                            type="number"
                            min={0}
                            step={100}
                            value={item.member_price}
                            placeholder="なし"
                            onChange={(e) =>
                              update(cls.source_lesson_id, {
                                member_price: e.target.value,
                              })
                            }
                          />
                        </label>
                      </div>
                      {check.errors.map((m) => (
                        <p key={m} className="sa-error-text">
                          {m}
                        </p>
                      ))}
                      {check.warnings.map((m) => (
                        <p key={m} className="sa-warn-text">
                          {m}
                        </p>
                      ))}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>

          <p className="review-note">
            定員・料金は直近の回の値が入っています。登録するとすぐにホームページに掲載され、
            予約の受付が始まります。講師への通知は「確定して通知」で送ります。
          </p>
          {error ? <p className="sa-error-text">{error}</p> : null}
        </div>
        <div className="review-modal-foot">
          <button
            type="button"
            className="act act-ghost"
            onClick={close}
            disabled={sending}
          >
            キャンセル
          </button>
          <button
            type="button"
            className="act act-go"
            onClick={submit}
            disabled={sending || items.length === 0 || hasError}
          >
            {sending
              ? "登録中…"
              : items.length
                ? `${items.length}件を登録して掲載`
                : "登録して掲載"}
          </button>
        </div>
      </div>
    </div>
  );
}

// 提出内容の修正・代理提出。講師アプリと同じく、候補日ごとに 終日OK か時間帯を選ぶ。
function EditSubmissionModal({ row, month, candidates, onClose, onDone }) {
  const [drafts, setDrafts] = useState(() => {
    const map = {};
    (row.submission?.days || []).forEach((d) => {
      map[d.date] = {
        allDay: d.all_day,
        from: d.time_from || "",
        to: d.time_to || "",
      };
    });
    return map;
  });
  const [note, setNote] = useState(row.submission?.note || "");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);

  const toggle = (date) =>
    setDrafts((prev) => {
      const next = { ...prev };
      if (next[date]) delete next[date];
      else next[date] = { allDay: true, from: "", to: "" };
      return next;
    });
  const update = (date, patch) =>
    setDrafts((prev) => ({ ...prev, [date]: { ...prev[date], ...patch } }));

  const submit = async () => {
    if (sending) return;
    const dates = candidates.map((c) => c.date).filter((d) => drafts[d]);
    for (const date of dates) {
      const d = drafts[date];
      if (d.allDay) continue;
      const from = toMinutes(d.from);
      const to = toMinutes(d.to);
      if (from === null || to === null) {
        setError(`${dayLabel(date)}の時間帯を入力するか、「終日OK」を選択してください。`);
        return;
      }
      if (from >= to) {
        setError(`${dayLabel(date)}の時間帯は、終了を開始より後にしてください。`);
        return;
      }
    }
    setSending(true);
    setError(null);
    try {
      await slashAppMonthly.saveSubmission(row.id, month, {
        note,
        days: dates.map((date) => ({
          date,
          all_day: drafts[date].allDay,
          time_from: drafts[date].allDay ? null : drafts[date].from,
          time_to: drafts[date].allDay ? null : drafts[date].to,
        })),
      });
      await onDone();
    } catch (e) {
      console.error(e);
      setError(slashAppErrorMessage(e, "保存に失敗しました"));
      setSending(false);
    }
  };

  const close = () => {
    if (!sending) onClose();
  };

  return (
    <div
      className="review-overlay"
      onClick={(e) => e.target === e.currentTarget && close()}
    >
      <div className="review-modal sa-modal" role="dialog" aria-modal="true">
        <div className="review-modal-head">
          <h3>
            {row.username} の{monthLabel(month)}分のシフト
            {row.submission ? "を編集" : "を代理で提出"}
          </h3>
          <button type="button" className="review-x" onClick={close} aria-label="閉じる">
            ×
          </button>
        </div>
        <div className="review-modal-body">
          <div className="review-label">出られる日</div>
          <div className="sa-edit-days">
            {candidates.map((c) => {
              const d = drafts[c.date];
              return (
                <div key={c.date} className={`sa-edit-day ${d ? "is-on" : ""}`}>
                  <label className="sa-edit-check">
                    <input
                      type="checkbox"
                      checked={!!d}
                      onChange={() => toggle(c.date)}
                      disabled={sending}
                    />
                    {dayLabel(c.date)}
                    {extraLabel(c.label) ? (
                      <span className="sa-day-extra">{extraLabel(c.label)}</span>
                    ) : null}
                  </label>
                  {d ? (
                    <div className="sa-edit-time">
                      <label>
                        <input
                          type="checkbox"
                          checked={d.allDay}
                          onChange={() => update(c.date, { allDay: !d.allDay })}
                          disabled={sending}
                        />
                        終日OK
                      </label>
                      {!d.allDay ? (
                        <>
                          <input
                            type="time"
                            step={300}
                            value={d.from}
                            onChange={(e) => update(c.date, { from: e.target.value })}
                          />
                          〜
                          <input
                            type="time"
                            step={300}
                            value={d.to}
                            onChange={(e) => update(c.date, { to: e.target.value })}
                          />
                        </>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
          <label className="review-label" htmlFor="sa-note">
            備考<span className="optional">任意</span>
          </label>
          <textarea
            id="sa-note"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <p className="review-note">
            講師への通知はありません。日を1つも選ばずに保存すると「出られる日なし」になります。
          </p>
          {error ? <p className="sa-error-text">{error}</p> : null}
        </div>
        <div className="review-modal-foot">
          <button
            type="button"
            className="act act-ghost"
            onClick={close}
            disabled={sending}
          >
            キャンセル
          </button>
          <button
            type="button"
            className="act act-go"
            onClick={submit}
            disabled={sending}
          >
            {sending ? "保存中…" : "保存する"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default MonthlyShiftAdjustPanel;
